import type { EffectRegion } from "@client/types/vision";
import type React from "react";
import { RegionControls } from "./RegionControls";

interface ContrastToolProps {
	level: number;
	roiLevel: number;
	preset?: "Low" | "Normal" | "High";
	mode?: "grayscale" | "warm" | "cool";
	onChange: (params: {
		level: number;
		preset?: "Low" | "Normal" | "High";
		mode?: "grayscale" | "warm" | "cool";
	}) => void;
	onRoiLevelChange: (value: number) => void;
	effectRegion: EffectRegion | null;
	regionDrawMode: "none" | "drawing";
	regionPoints: Array<{ x: number; y: number }>;
	onStartDrawing: () => void;
	onUndoPoint: () => void;
	onCancelDrawing: () => void;
	onClearRegion: () => void;
}

export const ContrastTool: React.FC<ContrastToolProps> = ({
	level,
	roiLevel,
	preset,
	mode,
	onChange,
	onRoiLevelChange,
	effectRegion,
	regionDrawMode,
	regionPoints,
	onStartDrawing,
	onUndoPoint,
	onCancelDrawing,
	onClearRegion,
}) => {
	return (
		<div className="p-4">
			<div className="mb-3 font-semibold">Contrast Sensitivity</div>

			<RegionControls
				effectRegion={effectRegion}
				regionDrawMode={regionDrawMode}
				regionPoints={regionPoints}
				onStartDrawing={onStartDrawing}
				onUndoPoint={onUndoPoint}
				onCancelDrawing={onCancelDrawing}
				onClearRegion={onClearRegion}
			/>

			{/* Region slider FIRST when a region is drawn (the user's primary
			    intent after drawing). Base slider second. */}
			{effectRegion && (
				<div className="mb-3">
					<div className="text-lg font-bold tabular-nums mb-1">
						Region level {roiLevel.toFixed(2)}
					</div>
					<input
						type="range"
						min={0}
						max={2}
						step={0.01}
						value={roiLevel}
						onChange={(e) => onRoiLevelChange(Number(e.target.value))}
						className="w-full"
						aria-label="Region contrast level"
					/>
				</div>
			)}

			{/* Preset buttons - wrap on small screens */}
			<div className="flex flex-wrap gap-2 mb-2">
				{["Low", "Normal", "High"].map((p) => (
					<button
						key={p}
						className={`px-3 py-1 text-sm rounded ${preset === p ? "bg-blue-500 text-white" : "bg-gray-200"}`}
						onClick={() => onChange({ level, preset: p as any, mode })}
					>
						{p}
					</button>
				))}
			</div>
			{effectRegion && (
				<div className="text-xs text-gray-600 mb-1">
					Base level {level.toFixed(2)}
				</div>
			)}
			<input
				type="range"
				min={0}
				max={2}
				step={0.01}
				value={level}
				onChange={(e) =>
					onChange({ level: Number(e.target.value), preset, mode })
				}
				className="w-full"
				aria-label="Base contrast level"
			/>
			{/* Mode buttons - wrap on small screens */}
			<div className="flex flex-wrap gap-2 mt-2">
				{["grayscale", "warm", "cool"].map((m) => (
					<button
						key={m}
						className={`px-3 py-1 text-sm rounded ${mode === m ? "bg-blue-500 text-white" : "bg-gray-200"}`}
						onClick={() => onChange({ level, preset, mode: m as any })}
					>
						{m}
					</button>
				))}
			</div>
		</div>
	);
};
